import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next/typescript/t";
import { SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";

import { FilterField } from "@/components_v2/Filters/types";
import FilterRenderer from "@/components_v2/Filters/FilterRenderer";
import { useFilterCount } from "@/shared/hooks/useFilterCount";
import {
  HookFormBaseDropdown,
  HookFormDateRangePicker,
  HookFormInfiniteScrollDropdown,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import {
  useGetDataFileExtensionsQuery,
  useLazyGetUsersQuery,
} from "@/redux/service";
import { BaseUserResponse, UserQueryParams } from "@/redux/users/types";
import {
  DataFilesFiltersProps,
  DataFilesFiltersState,
} from "@/features/files/list/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";

const dataFileFiltersSchema = (t: TFunction) =>
  yup
    .object()
    .shape({
      extensions: yup
        .array()
        .of(
          yup
            .string()
            .typeError(t("datablocks.errors.notValid"))
            .required(t("datablocks.errors.required")),
        )
        .default([]),
      search: yup
        .string()
        .typeError(t("datablocks.errors.notValid"))
        .transform((value) => (value === "" ? undefined : value))
        .matches(/^[^<>*]+$/, t("datablocks.errors.specialCharsNotAllowed"))
        .max(100, t("datablocks.errors.searchTooLong")),
      uploaders: yup
        .array()
        .of(yup.number().typeError(t("datablocks.errors.notValid")))
        .typeError(t("datablocks.errors.notValid")),
      uploadedAt: yup
        .array()
        .of(yup.string().typeError(t("datablocks.errors.notValid")))
        .length(2, t("datablocks.errors.notValid"))
        .typeError(t("datablocks.errors.notValid"))
        .nullable(),
    })
    .required();

const DataFilesFilters = ({
  onFiltersChange,
  filterNames,
  defaultFilters,
}: DataFilesFiltersProps) => {
  const { t } = useTranslation();

  const { data: dataFileExtensions } = useGetDataFileExtensionsQuery();
  const currentUser = useSelector(selectCurrentUser);
  const [getUsers] = useLazyGetUsersQuery();

  const form = useForm<DataFilesFiltersState>({
    // @ts-expect-error filters state is same as schema
    resolver: yupResolver(dataFileFiltersSchema(t)),
    mode: "onChange",
    defaultValues: {
      ...defaultFilters,
      search: undefined,
      extensions: [],
      uploadedAt: undefined,
      uploaders: [],
    },
  });
  const activeFiltersCount = useFilterCount<DataFilesFiltersState>(
    form,
    defaultFilters,
  );

  const filters: Record<string, FilterField> = useMemo(() => {
    return {
      extensions: {
        name: "extensions",
        isAdvancedFilter: true,
        label: t("datablocks.labels.fileExtensions"),
        component: (
          <HookFormBaseDropdown
            name="extensions"
            control={form.control}
            options={
              dataFileExtensions
                ? dataFileExtensions.extensions.map((extension) => ({
                    value: extension,
                    label: extension.replace(".", "").toUpperCase(),
                  }))
                : []
            }
            multiple
            placeholder={t("datablocks.placeholders.extensions")}
            allowClear={true}
          />
        ),
      },
      uploadedAt: {
        name: "uploadedAt",
        isAdvancedFilter: true,
        label: t("datablocks.labels.fileUploadedAt"),
        component: (
          <HookFormDateRangePicker
            name="uploadedAt"
            control={form.control}
            placeholder={[
              t("datablocks.placeholders.startDate"),
              t("datablocks.placeholders.endDate"),
            ]}
          />
        ),
      },
      ...(currentUser && {
        uploadedBy: {
          name: "uploaders",
          isAdvancedFilter: true,
          label: t("datablocks.labels.fileUploadedBy"),
          component: (
            <HookFormInfiniteScrollDropdown<BaseUserResponse, UserQueryParams>
              name="uploaders"
              control={form.control}
              fetchFn={getUsers}
              multiple={true}
              labelFieldName="fullName"
              valueFieldName="id"
              lookupFieldName="ids"
              searchFieldName="search"
              placeholder={t("datablocks.placeholders.uploader")}
              defaultFilters={{
                organizations: currentUser.organization?.id,
              }}
              allowClear={true}
            />
          ),
        },
      }),
      search: {
        name: "search",
        isDebounced: true,
        isAdvancedFilter: false,
        component: (
          <HookFormInput
            name="search"
            suffix={<SearchOutlined />}
            placeholder={t("datablocks.placeholders.searchFile")}
            control={form.control}
          />
        ),
      },
    };
  }, [dataFileExtensions, currentUser]);

  return (
    <FilterRenderer<DataFilesFiltersState>
      filters={filters}
      activeFilterNames={filterNames}
      onFiltersChange={onFiltersChange}
      form={form}
      activeFilterCount={activeFiltersCount}
      popoverTitle={t("datablocks.labels.filters")}
      handleClear={() => {
        form.reset(defaultFilters);
        onFiltersChange(form.getValues());
      }}
      handleSearch={() => {
        const formData = form.getValues();
        const filteringData = {
          ...formData,
          uploadedAt: undefined,
          uploadedAtAfter: undefined as string | undefined | null,
          uploadedAtBefore: undefined as string | undefined | null,
        };
        const dateRangeFilters = formData.uploadedAt;
        if (dateRangeFilters) {
          filteringData.uploadedAtAfter = dateRangeFilters[0]?.toISO();
          filteringData.uploadedAtBefore = dateRangeFilters[1]?.toISO();
        }

        onFiltersChange(filteringData);
      }}
      className="filter-layout"
    />
  );
};

export default DataFilesFilters;
